import { useCallback } from 'react';

/**
 * Hook for copying text to clipboard
 * Returns a callback function that copies the provided text to clipboard
 * 
 * @returns A function that copies text to clipboard
 * 
 * @example
 * const copyToClipboard = useCopyToClipboard();
 * copyToClipboard('some-id-123');
 */
export const useCopyToClipboard = () => {
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (fallbackErr) {
        console.error('Failed to copy to clipboard:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  }, []);

  return copyToClipboard;
};
